// @ts-nocheck
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { states } from '@/lib/constants';
import MobileSelect from './MobileSelect';

type CoachType = 'club_coach' | 'college_coach';

const US_STATES = states.map(s => ({ value: s.value, label: s.label }));

interface MobileCoachFormProps {
  coachType: CoachType;
  onBack: () => void;
  onSuccess?: () => void;
}

const MobileCoachForm: React.FC<MobileCoachFormProps> = ({ coachType, onBack, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    username: '',
    password: '',
    confirmPassword: '',
    clubName: '',
    collegeName: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      if (file.size > 5242880) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setLogoFile(file);
    }
  };

  const uploadLogo = async (userId: string): Promise<string | null> => {
    if (!logoFile) return null;

    try {
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${userId}/logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('team-logos')
        .upload(fileName, logoFile, { upsert: true });

      if (uploadError) {
        console.error('Logo upload error:', uploadError);
        return null;
      }

      const { data } = supabase.storage.from('team-logos').getPublicUrl(fileName);
      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      return null;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const requiredFields = ['name', 'email', 'phone', 'city', 'state', 'username', 'password', 'confirmPassword'];
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
      }
    }

    if (coachType === 'club_coach' && !formData.clubName) {
      newErrors.clubName = 'Club name is required';
    }
    if (coachType === 'college_coach' && !formData.collegeName) {
      newErrors.collegeName = 'College name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (coachType === 'college_coach' && formData.email && !formData.email.endsWith('.edu')) {
      newErrors.email = 'College coaches must use a .edu email address';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const role = coachType;
      const primaryRoleSlug = coachType;
      const activeMembershipSlug = coachType;

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            username: formData.username,
            role: role
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user account');

      const logoUrl = await uploadLogo(authData.user.id);

      const profileData: any = {
        user_id: authData.user.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        username: formData.username,
        role: role,
        primary_role_slug: primaryRoleSlug,
        active_membership_slug: activeMembershipSlug,
        status: 'active',
        profile_completion: 75
      };

      if (coachType === 'club_coach' && formData.clubName) {
        profileData.club_name = formData.clubName;
      } else if (coachType === 'college_coach' && formData.collegeName) {
        profileData.college_name = formData.collegeName;
      }

      if (logoUrl) {
        profileData.coach_logo_url = logoUrl;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('user_id', authData.user.id);

      if (profileError) throw profileError;

      toast.success('Coach account created successfully!');
      onSuccess?.();
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'An unexpected error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  const title = coachType === 'club_coach' ? 'Create Club Coach Account' : 'Create College Coach Account';
  const orgLabel = coachType === 'club_coach' ? 'Club Name *' : 'College Name *';
  const orgPlaceholder = coachType === 'club_coach' ? 'Enter your club name' : 'Enter your college name';
  const logoLabel = coachType === 'club_coach' ? 'Club Logo (Optional)' : 'College Logo (Optional)';
  const orgField = coachType === 'club_coach' ? 'clubName' : 'collegeName';

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
        <h1 className="text-xl font-bold mt-2">{title}</h1>
      </div>

      {/* Form */}
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter your full name"
              disabled={isLoading}
              className="h-12"
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email * {coachType === 'college_coach' && <span className="text-xs text-muted-foreground">(must end in .edu)</span>}
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={coachType === 'college_coach' ? "coach@university.edu" : "Enter your email"}
              disabled={isLoading}
              className="h-12"
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Enter your phone number"
              disabled={isLoading}
              className="h-12"
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Enter your city"
              disabled={isLoading}
              className="h-12"
            />
            {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
          </div>

          {/* State */}
          <MobileSelect
            label="State *"
            placeholder="Select your state"
            options={US_STATES}
            value={formData.state}
            onChange={(val) => handleInputChange('state', val)}
            error={errors.state}
            disabled={isLoading}
          />

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder="Choose a unique username"
              disabled={isLoading}
              className="h-12"
            />
            {errors.username && <p className="text-sm text-destructive">{errors.username}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Create a password (min 8 characters)"
                disabled={isLoading}
                className="h-12 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="Confirm your password"
              disabled={isLoading}
              className="h-12"
            />
            {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
          </div>

          {/* Organization Name */}
          <div className="space-y-2">
            <Label htmlFor={orgField}>{orgLabel}</Label>
            <Input
              id={orgField}
              value={coachType === 'club_coach' ? formData.clubName : formData.collegeName}
              onChange={(e) => handleInputChange(orgField, e.target.value)}
              placeholder={orgPlaceholder}
              disabled={isLoading}
              className="h-12"
            />
            {errors[orgField] && <p className="text-sm text-destructive">{errors[orgField]}</p>}
          </div>

          {/* Logo Upload */}
          <div className="space-y-2">
            <Label htmlFor="logo">{logoLabel}</Label>
            <Input
              id="logo"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleLogoUpload}
              disabled={isLoading}
              className="h-12 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground"
            />
            {logoFile && <p className="text-sm text-muted-foreground">Selected: {logoFile.name}</p>}
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Coach Account'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MobileCoachForm;
